import useResourceLegacy from 'util/resource/useResourceLegacy';
import React, { useEffect, useState } from 'react';
import ImageCard from 'ui/views/cards/ImageCard';
import welcome_background from 'ui/illustrations/graphic-welcome-background-2.svg';
import { companiesApi } from 'apis/CompanyAPI/companies/companiesApi';
import { getOrUndefined } from 'util/resource';
import useCompanyRole from 'hooks/useCompanyRole';
import { CompanyMessageList, ICompany } from 'types/company';
import Button from 'ui/elements/buttons/Button';
import ButtonList from 'ui/elements/buttons/ButtonList';
import { startProductTour } from 'util/intercom/intercom';
import productTours from 'util/intercom/productTours';
import PleaseEnableCookiesDialog from 'CookieConsent/PleaseEnableCookiesDialog';
import { useCloseableMessage } from 'apis/CompanyAPI/users/useUiState';
import useLocalStorage from 'hooks/useLocalStorage';

type Props = {
  company: ICompany;
};

export default function WelcomeMessage({ company }: Props) {
  const companyId = company.id;
  const { canEditCompany } = useCompanyRole(company.slug);

  const {
    isClosed: hasReadWelcomeMessage,
    closeMessage,
    isLoading,
  } = useCloseableMessage(`has-read-company-overview-welcome-message-${companyId}`);

  // Temporary migration of old localstorage-state to the new uiState 2024-10-23
  const [isClosedLegacy] = useLocalStorage(`has-read-company-overview-welcome-message-${companyId}`, null);
  useEffect(() => {
    if (isClosedLegacy === true && !hasReadWelcomeMessage) {
      closeMessage();
    }
  }, [isLoading, hasReadWelcomeMessage]);

  const [messages] = useResourceLegacy(
    () => (canEditCompany ? companiesApi.messages.overview(companyId) : new Promise<CompanyMessageList>(() => {})),
    [companyId],
  );

  const welcomeMessage = getOrUndefined(messages)?.values?.filter(message => message.type === 'WelcomeOverview')[0];
  const [showEnableCookieDialog, setShowEnableCookieDialog] = useState(false);

  if (!welcomeMessage || hasReadWelcomeMessage || isLoading) {
    return null;
  }

  const intercomProductTourId = productTours.companyOverviewProductTourForAdmins();

  const onClick = () => {
    const _Intercom: Intercom_.IntercomStatic = (window as any).Intercom;
    if (!_Intercom) {
      setShowEnableCookieDialog(true);
    } else {
      intercomProductTourId && startProductTour(intercomProductTourId);
    }
  };
  return (
    <ImageCard
      backgroundImage={welcome_background}
      className="u-half-spacing-bottom text-font-text u-fade-in--quick"
      padding="double"
      style={{ backgroundPosition: 'right -140px bottom -10px' }}
      bleedOnMobile
    >
      <div style={{ maxWidth: 685 }}>
        <h2 className="u-half-spacing-bottom text-weight-medium text-x-large">Welcome to your company profile!</h2>
        <p className="u-content-spacing-bottom text-medium">
          This is your company's home base — where investors, followers and visitors stay in the loop. Share quick
          updates, milestones, and wins to build trust and momentum. Keep it fresh, stay visible, and let your growth
          story shine.
        </p>
        <ButtonList>
          {intercomProductTourId && (
            <Button kind="primary" color="blue" onClick={onClick}>
              Show me around
            </Button>
          )}
          <Button kind="tertiary" color="grey" onClick={closeMessage}>
            Dismiss
          </Button>
        </ButtonList>
      </div>
      <PleaseEnableCookiesDialog open={showEnableCookieDialog} onClose={() => setShowEnableCookieDialog(false)} />
    </ImageCard>
  );
}
